/**
 * SCENE 03: THE ALIGNMENT OF CHAOS (Classical Antiquity // 500 BCE)
 * 1,000 tumbling rock fragments in mid-air. Orbiting perspective locks them into marble archway.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';
import { globalPRNG } from '../core/PRNG';

export class Scene03_Alignment implements ISceneLifecycle {
  public id = 2;
  public name = 'The Alignment of Chaos';
  public era = 'Classical Antiquity (500 BCE)';

  private eventBus: EventBus;
  private store: StateStore;
  
  private currentAngle: number = 45.0; // Current orbit angle in degrees
  private targetAngle: number = 45.0;
  private targetAlignmentAngle: number = 0.0; // Exact mathematical alignment at 0 deg

  private isAligned: boolean = false;
  private fragments: Array<{ x: number; y: number; r: number; offsetAngle: number }> = [];

  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();

    // Generate 200 instanced tumbling stone fragments
    for (let i = 0; i < 200; i++) {
      this.fragments.push({
        x: globalPRNG.range(-200, 200),
        y: globalPRNG.range(-150, 150),
        r: globalPRNG.range(4, 12),
        offsetAngle: globalPRNG.range(-60, 60)
      });
    }
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, (payload) => {
      if (payload.dx) {
        this.targetAngle += payload.dx * 0.15;
      }
    });

    this.unsubscribeList.push(unsubMove);
  }

  public update(dt: number): void {
    // Lerp orbit angle
    this.currentAngle += (this.targetAngle - this.currentAngle) * (dt * 4.0);

    // Normalize angle to [-180, 180]
    let normalized = this.currentAngle % 360;
    if (normalized > 180) normalized -= 360;
    if (normalized < -180) normalized += 360;

    // Check if within +/- 2.5 degrees of alignment angle
    if (Math.abs(normalized - this.targetAlignmentAngle) < 2.5 && !this.isAligned) {
      this.isAligned = true;
      this.currentAngle = 0;
      this.targetAngle = 0;
      this.store.setFlag(StateBitmask.CHAOS_ALIGNED, true);
      this.eventBus.emit(EventType.AUDIO_TRIGGER_BELL);
      this.eventBus.emit(
        EventType.ACCESSIBILITY_ANNOUNCE,
        0, 0, 0, 0, 0, 1, 2,
        'Mathematical perspective alignment achieved! Tumbling rock fragments lock into an unbroken marble archway.'
      );
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    if (this.isAligned) {
      // Render Sunlit Geometric Marble Archway
      ctx.shadowColor = 'rgba(255, 235, 200, 0.9)';
      ctx.shadowBlur = 30;
      ctx.strokeStyle = 'rgba(255, 245, 230, 0.9)';
      ctx.lineWidth = 12;

      ctx.beginPath();
      ctx.arc(cx, cy + 40, 120, Math.PI, 0, false);
      ctx.stroke();

      ctx.font = '600 13px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.fillText('ALIGNMENT OF CHAOS  //  PERFECT HARMONY', cx, cy - 140);
    } else {
      // Render Tumbling Fragment Cloud in Mid-Air based on current orbit angle
      const rad = (this.currentAngle * Math.PI) / 180;
      ctx.fillStyle = 'rgba(200, 190, 180, 0.7)';

      for (let i = 0; i < this.fragments.length; i++) {
        const f = this.fragments[i];

        // Orbit rotation calculation
        const rotX = f.x * Math.cos(rad) - f.offsetAngle * Math.sin(rad);
        const rotY = f.y + Math.sin(rad + i) * 10;

        ctx.beginPath();
        ctx.arc(cx + rotX, cy + rotY, f.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.font = '400 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(180, 170, 160, 0.4)';
      ctx.fillText(`[ DRAG HORIZONTALLY TO ORBIT PERSPECTIVE: ${Math.round(this.currentAngle)}° / 0° ]`, cx, height - 40);
    }

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
